import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./QuickStats.css";
import apiClient from "../../api";

const QuickStats = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    aarti: 0,
    chalisa: 0,
    bhajan: 0,
    users: 0,
    pandits: 0,
    horoscope: 0,
    mantras: 0,
    panchang: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);

      const [
        aartiRes,
        chalisaRes,
        bhajanRes,
        usersRes,
        panditsRes,
        horoscopeRes,
        mantrasRes,
        panchangRes,
      ] = await Promise.allSettled([
        apiClient.get("/api/aarti/list/"),
        apiClient.get("/api/chalisa/list/"),
        apiClient.get("/api/bhajan_sangrah/categories/"),
        apiClient.get("/api/user/users/admin/"),
        apiClient.get("/api/pandit/pandits/"),
        apiClient.get("/api/rashi/horoscope/admin/"),
        apiClient.get("/api/mantras/categories/"),
        apiClient.get("/api/panchang/sections/"),
      ]);

      const newStats = {
        aarti: 0,
        chalisa: 0,
        bhajan: 0,
        users: 0,
        pandits: 0,
        horoscope: 0,
        mantras: 0,
        panchang: 0,
      };

      if (aartiRes.status === "fulfilled") {
        const data = aartiRes.value.data;
        newStats.aarti = data.count !== undefined ? data.count : (Array.isArray(data) ? data.length : 0);
      }

      if (chalisaRes.status === "fulfilled") {
        const data = chalisaRes.value.data;
        newStats.chalisa = data.count !== undefined ? data.count : (Array.isArray(data) ? data.length : 0);
      }

      if (bhajanRes.status === "fulfilled") {
        const data = bhajanRes.value.data;
        if (data.count !== undefined) {
          newStats.bhajan = data.count;
        } else if (Array.isArray(data)) {
          const categoryDetails = await Promise.all(
            data.map((c) =>
              apiClient.get(`/api/bhajan_sangrah/categories/${c.id}/`).catch(() => null)
            )
          );
          newStats.bhajan = categoryDetails.reduce(
            (total, res) => total + (res?.data?.bhajans?.length || 0),
            0
          );
        }
      }

      if (usersRes.status === "fulfilled") {
        const data = usersRes.value.data;
        newStats.users = data.count !== undefined ? data.count : (Array.isArray(data) ? data.length : 0);
      }

      if (panditsRes.status === "fulfilled") {
        const data = panditsRes.value.data;
        newStats.pandits = Array.isArray(data) ? data.length : (data.count || data.results?.length || 0);
      }

      if (horoscopeRes.status === "fulfilled") {
        newStats.horoscope = Array.isArray(horoscopeRes.value.data) ? horoscopeRes.value.data.length : 0;
      }

      if (mantrasRes.status === "fulfilled") {
        const data = mantrasRes.value.data;
        const categories = Array.isArray(data) ? data : (data.results || []);
        if (categories.length > 0) {
          const categoryDetails = await Promise.all(
            categories.map((c) =>
              apiClient.get(`/api/mantras/categories/${c.id}/`).catch(() => null)
            )
          );
          newStats.mantras = categoryDetails.reduce(
            (total, res) => total + (res?.data?.mantras?.length || 0),
            0
          );
        }
      }

      if (panchangRes.status === "fulfilled") {
        newStats.panchang = Array.isArray(panchangRes.value.data)
          ? panchangRes.value.data.reduce((sum, section) => sum + (section.date_count || 0), 0)
          : 0;
      }

      setStats(newStats);
      setError(null);
    } catch (err) {
      console.error("Error fetching stats:", err);
      setError("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  // Stat cards configuration with routes
  const statCards = [
    { key: "aarti", label: "Total Aartis", icon: "🕯️", route: "/admin/aarti", color: "#FF6B35" },
    { key: "chalisa", label: "Total Chalisas", icon: "📜", route: "/admin/chalisa", color: "#4CAF50" },
    { key: "bhajan", label: "Total Bhajans", icon: "🎵", route: "/admin/bhajan", color: "#2196F3" },
    { key: "users", label: "Total Users", icon: "👥", route: "/admin/users", color: "#9C27B0" },
    { key: "pandits", label: "Total Pandits", icon: "🙏", route: "/admin/pandits", color: "#FF9800" },
    { key: "horoscope", label: "Horoscopes", icon: "🔮", route: "/admin/horoscope", color: "#E91E63" },
    { key: "mantras", label: "Total Mantras", icon: "📿", route: "/admin/mantras", color: "#00BCD4" },
    { key: "panchang", label: "Panchang Data", icon: "🗓️", route: "/admin/panchang", color: "#795548" },
  ];

  // Quick action cards
  const quickActions = [
    { label: "Manage Users", description: "View and manage all users", icon: "👥", route: "/admin/users", color: "#4CAF50" },
    { label: "Puja Bookings", description: "Review booking requests", icon: "🛕", route: "/admin/bookings", color: "#2196F3" },
    { label: "Transactions", description: "View payment history", icon: "💳", route: "/admin/transactions", color: "#9C27B0" },
    { label: "Notifications", description: "Send app notifications", icon: "🔔", route: "/admin/notifications", color: "#FF9800" },
  ];

  const handleCardClick = (route) => {
    navigate(route);
  };

  if (loading) {
    return (
      <div className="quick-stats-page">
        <div className="qs-loading">
          <div className="qs-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quick-stats-page">
        <div className="qs-error">
          <span>⚠️</span>
          <p>{error}</p>
          <button onClick={fetchStats}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="quick-stats-page">
      {/* Welcome Header - FIRST */}
      <header className="qs-header">
        <div className="qs-header-left">
          <h1>Welcome back, Admin</h1>
          <p>Here's what's happening with your platform today</p>
        </div>
        <div className="qs-header-right">
          <div className="qs-admin-badge">
            <div className="qs-avatar">A</div>
            <div className="qs-admin-text">
              <span className="qs-admin-name">Admin</span>
              <span className="qs-admin-role">Administrator</span>
            </div>
          </div>
        </div>
      </header>

      {/* Overview Stats Section - SECOND */}
      <section className="qs-section">
        <div className="qs-section-title">
          <span className="qs-title-bar"></span>
          <h2>Overview</h2>
        </div>
        <div className="qs-cards-grid">
          {statCards.map((card) => (
            <div
              key={card.key}
              className="qs-stat-card"
              onClick={() => handleCardClick(card.route)}
            >
              <div className="qs-stat-icon" style={{ backgroundColor: card.color }}>
                {card.icon}
              </div>
              <div className="qs-stat-info">
                <span className="qs-stat-number">{stats[card.key]}</span>
                <span className="qs-stat-label">{card.label}</span>
              </div>
              <div className="qs-stat-link" style={{ color: card.color }}>View Details →</div>
            </div>
          ))}
        </div>
      </section>

      {/* Quick Actions Section - THIRD */}
      <section className="qs-section">
        <div className="qs-section-title">
          <span className="qs-title-bar"></span>
          <h2>Quick Actions</h2>
        </div>
        <div className="qs-actions-grid">
          {quickActions.map((action, index) => (
            <div
              key={index}
              className="qs-action-card"
              onClick={() => handleCardClick(action.route)}
            >
              <div className="qs-action-icon" style={{ backgroundColor: action.color }}>
                {action.icon}
              </div>
              <div className="qs-action-info">
                <h3>{action.label}</h3>
                <p>{action.description}</p>
              </div>
              <span className="qs-action-go">Go →</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default QuickStats;
